import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import getPlaceholderColors, { AGENT_MENTION_COLORS } from '../../utils/placeholderColors';
import { PLACEHOLDER_ERROR_COLOR } from '../../utils/PlaceholderUtil';

const ChipRoot = styled('span', {
  shouldForwardProp: p => !['chipBg', 'chipBorder', 'chipColor', 'size', 'clickable', 'wrapText'].includes(p),
})(({ theme, chipBg, chipBorder, chipColor, size, clickable, wrapText }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    display: wrapText ? 'inline-block' : 'inline-flex',
    alignItems: wrapText ? undefined : 'center',
    whiteSpace: wrapText ? 'normal' : 'nowrap',
    wordBreak: wrapText ? 'break-word' : undefined,
    overflowWrap: wrapText ? 'break-word' : undefined,
    height: wrapText ? 'auto' : (size === 'small' ? 22 : 26),
    lineHeight: wrapText ? '1.5' : (size === 'small' ? '20px' : '24px'),
    borderRadius: 5,
    boxSizing: 'border-box',
    padding: size === 'small' ? '1px 6px 1px 22px' : '2px 8px 2px 30px',
    overflow: 'hidden',
    fontSize: size === 'small' ? 11 : 13,
    fontWeight: 500,
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    letterSpacing: '-0.01em',
    maxWidth: '100%',
    position: 'relative',
    backgroundColor: chipBg,
    border: `1px solid ${chipBorder}`,
    color: chipColor || theme.palette.text.primary,
    cursor: clickable ? 'pointer' : 'default',
    transition: 'all 0.15s ease',
    boxShadow: isDark
      ? '0 1px 2px rgba(0, 0, 0, 0.2)'
      : '0 1px 2px rgba(0, 0, 0, 0.04)',
    '&:hover': clickable
      ? {
          boxShadow: isDark
            ? '0 2px 6px rgba(0, 0, 0, 0.3)'
            : '0 2px 4px rgba(0, 0, 0, 0.08)',
          filter: isDark ? 'brightness(1.15)' : 'brightness(0.97)',
        }
      : undefined,

    '&::before': {
      content: '""',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      position: 'absolute',
      width: size === 'small' ? 12 : 14,
      height: size === 'small' ? 12 : 14,
      left: size === 'small' ? 5 : 8,
      top: '50%',
      transform: 'translateY(-50%)',
      opacity: isDark ? 0.9 : 0.85,
    },
  };
});

const AgentChipRoot = styled('span', {
  shouldForwardProp: p => !['size', 'isDark'].includes(p),
})(({ theme, size, isDark }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  height: size === 'small' ? 22 : 26,
  lineHeight: size === 'small' ? '20px' : '24px',
  borderRadius: 5,
  boxSizing: 'border-box',
  padding: size === 'small' ? '1px 8px' : '2px 10px',
  overflow: 'hidden',
  fontSize: size === 'small' ? 11 : 13,
  fontWeight: 600,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  maxWidth: '100%',
  backgroundColor: isDark ? AGENT_MENTION_COLORS.bgDark : AGENT_MENTION_COLORS.bg,
  border: `1px solid ${isDark ? 'rgba(139, 92, 246, 0.4)' : AGENT_MENTION_COLORS.border}`,
  color: isDark ? AGENT_MENTION_COLORS.textDark : AGENT_MENTION_COLORS.text,
  boxShadow: isDark
    ? '0 1px 2px rgba(0, 0, 0, 0.2)'
    : '0 1px 2px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.15s ease',
}));

const DeleteButton = styled('span')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 4,
    cursor: 'pointer',
    borderRadius: '50%',
    padding: 2,
    flexShrink: 0,
    transition: 'background-color 0.15s ease',
    color: 'inherit',
    opacity: 0.7,
    '& svg': { fontSize: 14 },
    '&:hover': {
      opacity: 1,
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },
  };
});

/**
 * Unified placeholder chip component.
 *
 * @param {string}  placeholderKey  - The key to display (without ${ } wrapper)
 * @param {string}  [prefix='$']   - Trigger prefix: '$', '!', '=', or '@' for agent
 * @param {string}  [type='text']  - Placeholder type for color lookup
 * @param {number}  [length]       - Data length hint for color lookup
 * @param {boolean} [isValid=true] - False renders error styling
 * @param {string}  [size]         - 'small' for compact chips (canvas nodes)
 * @param {boolean} [wrapText]     - Allow text wrapping (for long placeholders)
 * @param {function} [onDelete]    - If provided, renders a delete button
 * @param {function} [onClick]     - Click handler
 * @param {object}  [style]        - Additional inline styles
 * @param {string}  [className]    - Additional CSS class
 */
const PlaceholderChip = React.forwardRef(function PlaceholderChip(
  {
    placeholderKey,
    prefix = '$',
    type = 'text',
    length,
    isValid = true,
    size,
    wrapText = false,
    onDelete,
    onClick,
    style,
    className,
    children,
    ...rest
  },
  ref
) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (prefix === '@') {
    return (
      <AgentChipRoot
        ref={ref}
        size={size}
        isDark={isDark}
        style={style}
        className={className}
        {...rest}
      >
        @{placeholderKey}
      </AgentChipRoot>
    );
  }

  let chipBg, chipBorder, chipColor, iconUrl;

  if (!isValid) {
    chipBg = isDark ? 'rgba(176, 0, 32, 0.15)' : PLACEHOLDER_ERROR_COLOR.BACKGROUND;
    chipBorder = isDark ? 'rgba(239, 68, 68, 0.5)' : PLACEHOLDER_ERROR_COLOR.BORDER;
    chipColor = isDark ? '#f87171' : PLACEHOLDER_ERROR_COLOR.TEXT;
    iconUrl = PLACEHOLDER_ERROR_COLOR.ICON;
  } else {
    const colors = getPlaceholderColors(type, length, isDark);
    chipBg = colors.hover;
    chipBorder = isDark ? `${colors.default}66` : colors.default;
    chipColor = colors.text;
    iconUrl = colors.icon;
  }

  const label = children || `${prefix}\u007B${placeholderKey}\u007D`;

  return (
    <ChipRoot
      ref={ref}
      chipBg={chipBg}
      chipBorder={chipBorder}
      chipColor={chipColor}
      size={size}
      clickable={!!onClick}
      wrapText={wrapText}
      onClick={onClick}
      style={{ ...style, '--chip-icon': `url(${iconUrl})` }}
      sx={{ '&::before': { backgroundImage: 'var(--chip-icon)' } }}
      className={className}
      {...rest}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {onDelete && (
        <DeleteButton
          role="button"
          tabIndex={-1}
          onClick={e => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
        >
          <CloseIcon />
        </DeleteButton>
      )}
    </ChipRoot>
  );
});

export default PlaceholderChip;
